import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '../../lib/cn';
import { formatPercentSigned } from '../../lib/format';
import { Card, CARD_FILL_CLASS, type CardFill } from './Card';
import { Skeleton } from './Skeleton';
import { StatIcon, type StatKind } from './stat-icons';

/**
 * The one KPI tile.
 *
 * There were three: this one, `TeamStatCard` (a "local twin" that wanted a slot
 * under the value) and `InvoicesListPage#KpiTile` (a copy that wanted to be
 * clickable). Three shells meant three paddings, three value type-scales and
 * three sets of hint behaviour on the four screens a person reads first. The
 * extra slots those two needed now live here — `footer`, `to`/`onClick` — so a
 * tile that navigates and a tile that just reports are the same object.
 */
export interface StatCardProps {
  /** ReactNode so a card can drop a qualifier on small screens rather than
   *  wrap to a third line and pull its neighbour's figure out of alignment. */
  label: ReactNode;
  value: ReactNode;
  /** Small line under the value, e.g. "vs. $12,400 last month" */
  hint?: ReactNode;
  /** Percent delta ratio (0.12 = +12%). Colors + arrow are automatic. */
  delta?: number;
  /** Trailing qualifier on the delta chip, e.g. "vs Jul". */
  deltaSuffix?: ReactNode;
  /** Set when a positive delta is bad (e.g. expenses). */
  invertDeltaColor?: boolean;
  /**
   * How many lines the hint may take before it is clipped. Default 1 (the hint
   * truncates on one line). Use 2 where the hint carries real information —
   * "incl. $869 expen…" inside a KPI card is a craft failure, not density.
   */
  hintLines?: 1 | 2;
  /**
   * Rendered under the hint: a sparkbar, a mini meter, a micro-CTA. Lets a tile
   * carry its own progress instead of a duplicate full-width bar restating the
   * same number 90px lower.
   *
   * ⚠ It is pinned to the tile's BOTTOM edge (`mt-auto`) — the app-wide fill
   * rule applied to a KPI tile. A strip stretches every tile to the tallest, so
   * without this /team's four tiles printed their meters at four different
   * heights with up to 76px of dead white under two of them. Pinned, the meters
   * form one baseline across the strip and the slack lands where it belongs:
   * between the hint and the meter.
   */
  footer?: ReactNode;
  /**
   * The financial concept this tile measures — renders the one glyph that
   * concept owns (`stat-icons.tsx`).
   *
   * **Every KPI tile carries an icon**, index and detail alike, and it comes
   * from here. Prefer `kind` over `icon`: passing the element yourself is how
   * `/budgets` ended up with a raw `<Target/>` next to `<StatIcon kind="budget"
   * />` on the tile beside it, and how one tile in a strip of three ended up
   * with no glyph at all while its neighbours had one.
   */
  kind?: StatKind;
  /** Escape hatch for a glyph that is genuinely not a financial concept.
   *  If you are reaching for it to draw a *concept*, add a `StatKind` instead. */
  icon?: ReactNode;
  /** Recolours the figure — a tile whose number is the alarm (overdue AR). */
  valueClassName?: string;
  /**
   * Make the tile navigational. `to` renders a link, `onClick` a toggle button
   * (pair it with `active`). A figure you can act on should be reachable by
   * keyboard, so both render real interactive elements — never a div with a
   * click handler.
   */
  to?: string;
  onClick?: () => void;
  /** Toggle state for an `onClick` tile — draws the selected ring. */
  active?: boolean;
  loading?: boolean;
  /**
   * ⚠ **THE QUERY BEHIND THIS TILE FAILED, AND THE TILE MUST SAY SO.**
   *
   * A KPI tile is fed by its own request, which can fail while the table under
   * it loads perfectly — that is exactly what happened on `/team`, where three
   * of four tiles sat under a fully-loaded roster showing a shimmer and then a
   * silent em dash. Both readings are the app claiming a number it does not
   * have: a skeleton says "still working" after it has given up, and "—" is
   * the same glyph this app uses for *no data*, so a failure reads as a fact.
   *
   * Set it from the query's own `isError` and pass `onRetry` — the retry lives
   * ON the tile because the tile is where the reader is standing, and a page
   * reload is a bigger step than the one the failure costs (Jesse's rule).
   * Hint, delta and footer are suppressed: every one of them describes a figure
   * that did not arrive.
   */
  error?: boolean;
  /** The server's own sentence, printed small under "Couldn't load". */
  errorMessage?: string;
  /** Refetch this tile's query. Rendered as the tile's own Retry control. */
  onRetry?: () => void;
  /**
   * How tall this tile may grow in its row — the same dial as `Card`, and the
   * same default. A strip of tiles should stay uniform, so reach for
   * `natural` only where a tile shares a row with something that is not a tile
   * (a chart card) and would otherwise be stretched past anything it can use.
   */
  fill?: CardFill;
  className?: string;
}

export function StatCard({
  label,
  value,
  hint,
  delta,
  deltaSuffix,
  invertDeltaColor = false,
  hintLines = 1,
  footer,
  kind,
  icon,
  valueClassName,
  to,
  onClick,
  active = false,
  loading = false,
  error = false,
  errorMessage,
  onRetry,
  fill,
  className,
}: StatCardProps) {
  const deltaGood = delta !== undefined && (invertDeltaColor ? delta <= 0 : delta >= 0);
  // A failed tile never becomes a link or a toggle: its Retry is a real button
  // and nesting one inside a Link is both invalid and a trap for the reader,
  // who would navigate away from the very screen the retry belongs to.
  const interactive = Boolean(to || onClick) && !error;
  const quiet = loading || error;
  const glyph = kind ? <StatIcon kind={kind} /> : icon;

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="text-caption font-semibold uppercase tracking-wider text-grey-500">{label}</p>
        {glyph && <span className="text-grey-400 shrink-0 [&>svg]:h-5 [&>svg]:w-5">{glyph}</span>}
      </div>
      {loading ? (
        <Skeleton className="mt-2 h-8 w-28" />
      ) : error ? (
        <div className="mt-1.5">
          <p className="flex items-center gap-1.5 text-body font-semibold text-status-late">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
            Couldn't load
          </p>
          {errorMessage && (
            <p className="mt-0.5 line-clamp-2 text-caption text-grey-500">{errorMessage}</p>
          )}
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-1.5 rounded text-body-sm font-semibold text-accent-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
            >
              Retry
            </button>
          )}
        </div>
      ) : (
        <p className={cn('mt-1 text-heading-2 text-secondary num truncate', valueClassName)}>
          {value}
        </p>
      )}
      {(delta !== undefined || hint) && !quiet && (
        <div
          className={cn(
            'mt-1.5 gap-2 text-body-sm',
            hintLines === 2 ? 'flex flex-wrap items-start' : 'flex items-center',
          )}
        >
          {delta !== undefined && (
            <span
              className={cn(
                'inline-flex items-center gap-1 font-semibold num',
                deltaGood ? 'text-success' : 'text-primary-600',
              )}
            >
              {delta >= 0 ? (
                <TrendingUp className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <TrendingDown className="h-3.5 w-3.5" aria-hidden />
              )}
              {/* A delta is a variance: explicit sign, and the sign is the app's
                  one minus glyph. `toFixed()` emitted an ASCII hyphen, which is
                  how /team printed "-13.4%" under a strip of "−" everywhere
                  else. */}
              {formatPercentSigned(delta, { decimals: 1, fixed: true })}
              {deltaSuffix && <span className="font-normal text-grey-500">{deltaSuffix}</span>}
            </span>
          )}
          {hint && (
            <span
              className={cn(
                'min-w-0 text-grey-500',
                // `line-clamp-2` still bounds the card height, but it breaks at
                // a word rather than mid-word inside a number.
                hintLines === 2 ? 'line-clamp-2' : 'truncate',
              )}
            >
              {hint}
            </span>
          )}
        </div>
      )}
      {/* `mt-auto` — see the `footer` docs: the meter sits ON the tile's bottom
          edge, so a strip of stretched tiles shares one baseline instead of
          each printing its own band of dead white. `pt-2` keeps the minimum
          gap it used to have when the tile is exactly as tall as its content. */}
      {footer && !quiet && <div className="mt-auto pt-2">{footer}</div>}
    </>
  );

  if (!interactive) {
    return (
      <Card padding="md" fill={fill} className={cn('min-w-0', className)}>
        {body}
      </Card>
    );
  }

  // Same shell, drawn on a real interactive element. `p-3 sm:p-6` rather than
  // Card's flat `p-6`: four tappable tiles plus whatever sits under them used
  // to push the first row of the list most of a phone screen down.
  const shell = cn(
    'flex min-w-0 flex-col rounded-xl border bg-white p-3 text-left shadow-card sm:p-6',
    'transition-all duration-200 hover:border-grey-300 hover:shadow-elevated',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30',
    active ? 'border-secondary ring-1 ring-secondary' : 'border-grey-200/50',
    // The one table of fill classes, shared with `Card` — a navigational tile
    // and a reporting tile in the same strip must not behave differently.
    CARD_FILL_CLASS[fill ?? 'grow'],
    className,
  );

  if (to) {
    return (
      <Link to={to} className={shell}>
        {body}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className={shell}>
      {body}
    </button>
  );
}
